/**
 * Feature 014: Service Provider Engagements
 * API client for consultation requests
 */

import { apiClient } from '../../../core/api/client';
import type {
  ConsultationRequest,
  CreateConsultationRequestDto,
  UpdateNotesDto,
  DeclineEngagementDto,
  DuplicateCheckResponse,
  DailyLimitResponse,
} from '../types';

const BASE_PATH = '/consultations';

/**
 * Create a new consultation request
 *
 * Endpoint: POST /api/v1/consultations
 *
 * @param data - Consultation request data
 * @returns Created consultation request
 * @throws 400 if validation fails (duplicate request or rate limit exceeded)
 * @throws 401 if not authenticated
 * @throws 404 if provider not found
 */
export const createConsultationRequest = async (
  data: CreateConsultationRequestDto
): Promise<ConsultationRequest> => {
  const response = await apiClient.post<ConsultationRequest>(
    BASE_PATH,
    data
  );
  return response.data;
};

/**
 * Get all consultation requests for a service provider
 * Uses current user from JWT token (no userId parameter needed)
 *
 * Endpoint: GET /api/v1/consultations/provider
 *
 * @param status - Optional status filter (PENDING, ACCEPTED, DECLINED)
 * @returns Paginated list of consultation requests
 * @throws 401 if not authenticated
 * @throws 403 if not a service provider
 */
export const getProviderConsultationRequests = async (
  status?: string
): Promise<ConsultationRequest[]> => {
  const params = status ? { status } : undefined;
  const response = await apiClient.get<{ data: ConsultationRequest[] }>(
    `${BASE_PATH}/provider`,
    { params }
  );
  return response.data.data;
};

/**
 * Get all consultation requests for a care seeker
 * Uses current user from JWT token
 *
 * Endpoint: GET /api/v1/consultations/care-seeker
 *
 * @returns List of care seeker's consultation requests
 * @throws 401 if not authenticated
 */
export const getMyConsultationRequests = async (): Promise<ConsultationRequest[]> => {
  const response = await apiClient.get<{ data: ConsultationRequest[] }>(
    `${BASE_PATH}/care-seeker`
  );
  return response.data.data;
};

/**
 * Accept a consultation request
 *
 * Endpoint: PATCH /api/v1/consultation-requests/:id/accept
 *
 * @param id - Consultation request ID
 * @returns Updated consultation request
 * @throws 401 if not authenticated
 * @throws 403 if not the provider
 * @throws 404 if request not found
 */
export const acceptConsultationRequest = async (
  id: string
): Promise<ConsultationRequest> => {
  const response = await apiClient.patch<ConsultationRequest>(
    `${BASE_PATH}/${id}/accept`
  );
  return response.data;
};

/**
 * Decline a consultation request
 *
 * Endpoint: PATCH /api/v1/consultation-requests/:id/decline
 *
 * @param id - Consultation request ID
 * @param data - Decline reason (will be sent as message to care seeker)
 * @returns Updated consultation request
 * @throws 401 if not authenticated
 * @throws 403 if not the provider
 * @throws 404 if request not found
 */
export const declineConsultationRequest = async (
  id: string,
  data: DeclineEngagementDto
): Promise<ConsultationRequest> => {
  const response = await apiClient.patch<ConsultationRequest>(
    `${BASE_PATH}/${id}/decline`,
    data
  );
  return response.data;
};

/**
 * Update provider private notes for a consultation request
 *
 * Endpoint: PATCH /api/v1/consultation-requests/:id/notes
 *
 * @param id - Consultation request ID
 * @param data - Provider notes
 * @returns Updated consultation request
 * @throws 401 if not authenticated
 * @throws 403 if not the provider
 * @throws 404 if request not found
 */
export const updateConsultationNotes = async (
  id: string,
  data: UpdateNotesDto
): Promise<ConsultationRequest> => {
  const response = await apiClient.patch<ConsultationRequest>(
    `${BASE_PATH}/${id}/notes`,
    data
  );
  return response.data;
};

/**
 * Check for duplicate pending consultation request
 *
 * Endpoint: GET /api/v1/consultation-requests/check-duplicate
 *
 * @param careSeekerUserId - Care seeker's user ID
 * @param providerUserId - Provider's user ID
 * @returns Duplicate check result
 * @throws 401 if not authenticated
 */
export const checkDuplicateRequest = async (
  careSeekerUserId: string,
  providerUserId: string
): Promise<DuplicateCheckResponse> => {
  const response = await apiClient.get<DuplicateCheckResponse>(
    `${BASE_PATH}/check-duplicate`,
    {
      params: {
        careSeekerUserId,
        providerUserId,
        status: 'PENDING',
      },
    }
  );
  return response.data;
};

/**
 * Check daily consultation request count for rate limiting
 *
 * Endpoint: GET /api/v1/consultation-requests/daily-count
 *
 * @param careSeekerUserId - Care seeker's user ID
 * @returns Daily count and limit status
 * @throws 401 if not authenticated
 */
export const checkDailyRequestCount = async (
  careSeekerUserId: string
): Promise<DailyLimitResponse> => {
  const response = await apiClient.get<DailyLimitResponse>(
    `${BASE_PATH}/daily-count`,
    {
      params: { careSeekerUserId },
    }
  );
  return response.data;
};
